import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { Element } from 'react-scroll';
import { Link } from 'react-router-dom';
import { Modal, ModalBody } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import env from 'Env';
import imgixConfig from 'Config/imgix';
import ProductHeaderPrices from 'Components/Product/ProductHeader/ProductHeaderPrices';
import Drm from 'Components/Utility/Drm';
import ShowcaseCarousel from 'Features/Showcase/components/ShowcaseCarousel/ShowcaseCarousel';
import ProductEditionsAddToCartBtn from 'Components/Product/ProductEditions/ProductEditionsAddToCartBtn';

import 'Features/Showcase/components/ShowcaseEditions/ShowcaseEditions.scss';

export default function ShowcaseEditions({ visible, product }) {
  const [modal, setModal] = useState(false);

  const toggle = () => { setModal(!modal); };

  const screenshots = get(product, 'img', []);
  const editionsBackgroundSrc = get(screenshots, [2], {});
  const name = get(product, 'name', '');
  const editions = get(product, 'editions', []);
  const presale = get(product, 'presale', false);
  const freeToPlay = get(product, 'free_to_play', false);
  const giveaway = get(product, 'giveaway', false);
  const soldOut = get(product, 'sold_out', false);

  const clonedEditions = cloneDeep(editions);

  if (isEmpty(editions) && !isEmpty(product)) {
    clonedEditions.push(product);
  }

  const allEditionPoints = [];
  clonedEditions.forEach((edition) => {
    if (edition.editionPoints) {
      edition.editionPoints.forEach((point) => {
        if (!allEditionPoints.includes(point)) {
          allEditionPoints.push(point);
        }
      });
    }
  });

  const showSoldOutButton = soldOut && !giveaway && !freeToPlay && !presale;

  const slideWidths = {
    xs: 300,
    sm: 350,
    md: 350,
    lg: 400,
    xl: 358,
    xxl: 420,
  };

  return (
    <section className={classNames('ShowcaseSection', { 'ShowcaseSection--fadeIn': visible.includes('editions') })}>
      <Element name="ShowcaseEditions" className="ShowcaseEditions">
        <div className="ShowcaseEditions__foreground">
          <ShowcaseCarousel
            slideWidths={slideWidths}
            title={`${name} editions`}
            appearsOn={'editions'}
          >
            {clonedEditions.map(edition =>
              <div className="ShowcaseEditions__editions__card" key={edition.name}>
                <Link to={`/${edition.type}/${edition.slug}`} className="ShowcaseEditions__editions__card__cover">
                  <img
                    srcSet={`
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=1920&h=1080&fit=crop 1920w,
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=1280&h=720&fit=crop 1280w,
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=1130&h=636&fit=crop 1130w,
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=960&h=540&fit=crop 960w,
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=740&h=416&fit=crop 740w,
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=640&h=360&fit=crop 640w,
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=480&h=270&fit=crop 480w,
                      ${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=384&h=216&fit=crop 384w
                    `}
                    sizes="(min-width: 1520px) 420px, (min-width: 1200px) 358px, (min-width: 992px) 400px, (min-width: 768px) 350px, (min-width: 576px) 350px, 95vw"
                    src={`${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}&q=75&w=1920&h=1080&fit=crop 1920w`}
                    alt={edition.name}
                  />
                </Link>
                <div className="ShowcaseEditions__editions__card__content">
                  <div className="ShowcaseEditions__editions__card__content__name">
                    {edition.edition_name || edition.name}
                  </div>

                  {!isEmpty(allEditionPoints) && false &&
                    <div className="ShowcaseEditions__editions__card__content__points">
                      {allEditionPoints.map(point =>
                        <div className={classNames('ShowcaseEditions__editions__card__content__points__point', { active: edition.editionPoints.includes(point) })} key={point}>
                          <div className="ShowcaseEditions__editions__card__content__points__point__icon">
                            {edition.editionPoints.includes(point) &&
                              <FontAwesomeIcon icon={faCheck} />
                            }
                            {!edition.editionPoints.includes(point) &&
                              <FontAwesomeIcon icon={faXmark} />
                            }
                          </div>
                          <div className="ShowcaseEditions__editions__card__content__points__point__name">
                            {point}
                          </div>
                        </div>,
                      )}
                    </div>
                  }

                  <div className="ShowcaseEditions__editions__card__content__platformPrice">
                    <div className="ShowcaseEditions__editions__card__content__platformPrice__platform">
                      <div className="ShowcaseEditions__editions__card__content__platformPrice__platform__label">
                        <FormattedMessage id="showcaseEdition.card.platform" defaultMessage="Platform" />
                      </div>
                      <div className="ShowcaseEditions__editions__card__content__platformPrice__platform__drm">
                        <Drm drmType={'steam'} colorOverride="light" />
                        <div className="ShowcaseEditions__editions__card__content__platformPrice__platform__drm__icons">
                          {edition.platforms.windows &&
                            <FontAwesomeIcon icon={fabFaWindows} />
                          }
                          {edition.platforms.mac &&
                            <FontAwesomeIcon icon={fabFaApple} />
                          }
                          {edition.platforms.linux &&
                            <FontAwesomeIcon icon={fabFaLinux} />
                          }
                        </div>
                      </div>
                    </div>
                    <ProductHeaderPrices showcase product={edition} />
                  </div>

                  {!showSoldOutButton &&
                    <ProductEditionsAddToCartBtn
                      product={edition}
                      className="ShowcaseButton ShowcaseButton--primary"
                    />
                  }
                  {showSoldOutButton &&
                    <button disabled className="ShowcaseButton ShowcaseButton--primary">
                      <FormattedMessage id="showcaseEdition.card.button.sold-out" defaultMessage="Sold Out" />
                    </button>
                  }

                  <Link to={`/${edition.type}/${edition.slug}`} className="ShowcaseEditions__editions__card__content__link">
                    <FormattedMessage id="showcaseEdition.card.link" defaultMessage="Find Out More" />
                  </Link>
                </div>
              </div>,
            )}
          </ShowcaseCarousel>

          {clonedEditions.length > 1 &&
            <div className="ShowcaseEditions__compare">
              <button className="ShowcaseButton ShowcaseButton--outline" onClick={() => { toggle(); }}>
                <FormattedMessage id="showcaseEdition.title.compare" defaultMessage="Compare Editions" />
              </button>
            </div>
          }
        </div>

        <div className="ShowcaseEditions__background">
          <img
            srcSet={`
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=1920&h=1080&fit=crop 1920w,
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=1280&h=720&fit=crop 1280w,
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=1130&h=636&fit=crop 1130w,
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=960&h=540&fit=crop 960w,
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=740&h=416&fit=crop 740w,
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=640&h=360&fit=crop 640w,
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=480&h=270&fit=crop 480w,
              ${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=384&h=216&fit=crop 384w
            `}
            src={`${env.cdnUrl}/product/original/${editionsBackgroundSrc.slug}?${imgixConfig}&q=25&w=1920&h=1080&fit=crop 1920w`}
            sizes="100vw"
            alt={editionsBackgroundSrc.alt}
            className="ShowcaseProduct__top__background__img"
          />
        </div>

        <Modal isOpen={modal} toggle={toggle} className="SiteModal SiteModal--large SiteModal--dark ShowcaseEditionsModal">
          <ModalBody className="SiteModal__main">
            <div className="SiteModal__main__title">
              <FormattedMessage
                id="showcaseEdition.title.editions"
                defaultMessage="{name} Editions"
                values={{
                  name,
                }}
              />
            </div>
            <div className="ShowcaseEditionsModal__tableContainer">
              <table className="EditionTable">
                <thead>
                  <tr>
                    <th />
                    {clonedEditions.map(edition =>
                      <td key={edition.edition_name || edition.name}>
                        <div className="EditionTable__card">
                          <Link to={`/${edition.type}/${edition.slug}`} className="">
                            <img
                              src={`${env.cdnUrl}/product/original/${edition.cover}?${imgixConfig}`}
                              alt={edition.name}
                            />
                          </Link>
                          {edition.edition_name || edition.name}
                        </div>
                      </td>,
                    )}
                  </tr>
                </thead>
                <tbody>
                  {allEditionPoints.map(point =>
                    <tr key={point}>
                      <th>{point}</th>
                      {clonedEditions.map(edition =>
                        <td key={edition.edition_name || edition.name} className={classNames('EditionTable__icon', { 'EditionTable__icon--includes': edition.editionPoints.includes(point) }, { 'EditionTable__icon--excludes': !edition.editionPoints.includes(point) })}>
                          {edition.editionPoints.includes(point) &&
                            <FontAwesomeIcon icon={faCheck} />
                          }
                          {!edition.editionPoints.includes(point) &&
                            <FontAwesomeIcon icon={faXmark} />
                          }
                        </td>,
                      )}
                    </tr>,
                  )}
                </tbody>
              </table>
            </div>
          </ModalBody>
          <div className="SiteModal__closeButton">
            <button aria-label="close" onClick={() => toggle()}>
              <FontAwesomeIcon icon={faXmark} title="Close modal" />
            </button>
          </div>
        </Modal>
      </Element>
    </section>
  );
}

ShowcaseEditions.propTypes = {
  visible: PropTypes.arrayOf(PropTypes.string),
  product: PropTypes.shape(),
};

ShowcaseEditions.defaultProps = {
  visible: [],
  product: {},
};
